// components
import { Header } from "components/atoms/Typography";
import AverageMarginalAbatementCostByMeasureCategoryChart from "components/organisms/Charts/AverageMarginalAbatementCostByMeasureCategoryChart";
import AnnualCarbonEmissionsElectricityChart from "components/organisms/Charts/Recommendations/AnnualCarbonEmissionsElectricityChart";
import AnnualCarbonEmissionsNaturalGasChart from "components/organisms/Charts/Recommendations/AnnualCarbonEmissionsNaturalGasChart";
import CumulativeInvestmentByDecarbonizationChart from "components/organisms/Charts/Recommendations/CumulativeInvestmentByDecarbonizationChart";
import { ReportsCumulativeOperatingCostCard } from "components/organisms/Charts/Recommendations/CumulativeOperatingCostCard";
import FutureProjectionCards from "components/organisms/Charts/Recommendations/FutureProjectionCards";

// query hooks
import AnnualEmissionSavingsByMeasures from "components/organisms/Charts/Recommendations/AnnualEmissionSavingsByMeasures";
import { ReportsIncrementalNetPresentValueCard } from "components/organisms/Charts/Recommendations/CumulativeNetPresentValueCard";
import { useAnnualCarbonEmissionsCard } from "queries/Reports/Recommendations/useAnnualCarbonEmissionsCard";
import { useAnnualCarbonEmissionsChart } from "queries/Reports/Recommendations/useAnnualCarbonEmissionsChart";
import { useAnnualCarbonEmissionsElectricityChart } from "queries/Reports/Recommendations/useAnnualCarbonEmissionsElectricityChart";
import { useAnnualCarbonEmissionsNaturalGasChart } from "queries/Reports/Recommendations/useAnnualCarbonEmissionsNaturalGasChart";
import { useAnnualEnergyConsumptionCard } from "queries/Reports/Recommendations/useAnnualEnergyConsumptionCard";
import { useAverageMarginalAbatementCostByMeasureCategoryChart } from "queries/Reports/Recommendations/useAverageMarginalAbatementCostByMeasureCategoryChart";
import { useCumulativeInvestmentByDecarbonizationChart } from "queries/Reports/Recommendations/useCumulativeInvestmentByDecarbonizationChart";

// styling
import AnnualCarbonEmissionsChartWithRecs from "components/organisms/Charts/Recommendations/AnnualCarbonEmissionsChartWithRecs";
import { ReportsAnnualCashFlowChartRecommendations } from "components/organisms/Charts/Recommendations/AnnualCashFlowChartRecommendations";
import CumulativeCarbonPolicyCostChart from "components/organisms/Charts/Recommendations/CumulativeCarbonPolicyCostChart";
import FiltersSidebar from "components/organisms/FiltersSidebar";
import { useCumulativeCarbonEmissionsCard } from "queries/Reports/Recommendations/useCumulativeCarbonEmissionsCard";
import { useCumulativeEnergyConsumptionCard } from "queries/Reports/Recommendations/useCumulativeEnergyConsumptionCard";
import { useState } from "react";
import { CURRENT_YEAR } from "utils/constants";
import "./DecarbonizationReport.css";

function DecarbonizationReport({
    filtersetUid,
    reportedVsPublished,
}: {
    filtersetUid: string;
    reportedVsPublished: string;
}) {
    const [filterSidebarIsOpen, setFilterSidebarIsOpen] = useState(false);

    return (
        <div className="recommendations-report">
            <FiltersSidebar
                closeSidebar={() => setFilterSidebarIsOpen(false)}
                sidebarOpen={filterSidebarIsOpen}
                currentActiveTab="outcomes"
            />

            <Header>Reach your goals</Header>
            <AnnualEmissionSavingsByMeasures filtersetUid={filtersetUid} />
            <Header>Financial impact ({CURRENT_YEAR} - 2050)</Header>
            <div className="cards-and-chart-grid">
                <div className="cards">
                    <ReportsIncrementalNetPresentValueCard
                        filtersetUid={filtersetUid}
                        reportedVsPublished={reportedVsPublished}
                    />
                    <ReportsCumulativeOperatingCostCard
                        filtersetUid={filtersetUid}
                        reportedVsPublished={reportedVsPublished}
                        isBaseline={false}
                        year={null}
                    />
                </div>
                <ReportsAnnualCashFlowChartRecommendations
                    filtersetUid={filtersetUid}
                    reportedVsPublished={reportedVsPublished}
                />
            </div>
            <div className="side-by-side-charts-grid">
                <AverageMarginalAbatementCostByMeasureCategoryChart
                    query={() => useAverageMarginalAbatementCostByMeasureCategoryChart(filtersetUid)}
                />
                <CumulativeInvestmentByDecarbonizationChart
                    query={() => useCumulativeInvestmentByDecarbonizationChart(filtersetUid)}
                />
            </div>
            <Header>Future projection</Header>
            <div className="cards-and-chart-grid">
                <FutureProjectionCards
                    cumulativeCarbonEmissionsCardQuery={() => useCumulativeCarbonEmissionsCard(filtersetUid)}
                    cumulativeEnergyConsumptionCardQuery={() => useCumulativeEnergyConsumptionCard(filtersetUid)}
                    annualCarbonEmissionsCardQuery={() => useAnnualCarbonEmissionsCard(filtersetUid)}
                    annualEnergyConsumptionCardQuery={() => useAnnualEnergyConsumptionCard(filtersetUid)}
                />
                <AnnualCarbonEmissionsChartWithRecs query={() => useAnnualCarbonEmissionsChart(filtersetUid)} />
            </div>
            <div className="side-by-side-charts-grid">
                <AnnualCarbonEmissionsNaturalGasChart
                    query={() => useAnnualCarbonEmissionsNaturalGasChart(filtersetUid)}
                />
                <AnnualCarbonEmissionsElectricityChart
                    query={() => useAnnualCarbonEmissionsElectricityChart(filtersetUid)}
                />
            </div>
            <CumulativeCarbonPolicyCostChart filtersetUid={filtersetUid} reportedVsPublished={reportedVsPublished} />
        </div>
    );
}

export default DecarbonizationReport;
